<script setup>
import { ref, computed } from "vue";
import { useForm } from "vee-validate";
import * as yup from "yup";
import { customApi } from "@app/helpers";
import Box from "@templates/Box.vue";
import Message from "@components/Message.vue";
import FieldInputText from "@components/FieldInputText.vue";
import FieldSelect from "@components/FieldSelect.vue";
import FieldTextarea from "@components/FieldTextarea.vue";
import TurnstileWidget from "@components/TurnstileWidget.vue";

const { meta, handleSubmit, resetForm } = useForm();

// Anti-spam (inc/turnstile.php). The key comes from wp_localize_script in
// inc/enqueues.php; none means it's switched off.
const turnstileSiteKey = window.tjContact?.turnstileSiteKey || "";
const turnstileToken = ref("");
const turnstile = ref(null);
const canSend = computed(
  () => meta.value.valid && (!turnstileSiteKey || !!turnstileToken.value)
);

// Same rule as the Partner portal's contact form (InputTelField)
const numberSchema = yup
  .string()
  .required("Contact Number is required.")
  .matches(/^\+?[0-9]+$/, "Contact Number must be numeric")
  .max(13, "Contact Number is too long")
  .min(10, "Contact Number should be at least 10 digits");

const formMessage = ref({ severity: "", content: "" });
const isLoading = ref(false);

const onSubmit = handleSubmit(async (values) => {
  formMessage.value = { severity: "", content: "" }; // Clear previous messages
  isLoading.value = true;

  try {
    const response = await customApi.post("/contact", {
      ...values,
      turnstile_token: turnstileToken.value,
    });
    formMessage.value = {
      severity: "success",
      content: response.data.message,
    };
    resetForm(); // Clear the fields so the message isn't sent twice
  } catch (error) {
    console.error(error);
    formMessage.value = {
      severity: "error",
      content:
        error.response?.data?.message ||
        "Sorry, we couldn't send your message. Please try again later.",
    };
  } finally {
    // A token works once, sent or not
    turnstile.value?.reset();
    isLoading.value = false;
  }
});
</script>

<template>
  <section class="flex flex-col py-80 container">
    <h1 class="mb-60 text-25 md:text-39 text-center">Contact us</h1>

    <Box class="bg-white mx-auto !py-60 w-full max-w-[730px]">
      <Message
        v-if="formMessage.content"
        :key="formMessage.content"
        :severity="formMessage.severity"
        removable
        centered>
        {{ formMessage.content }}
      </Message>

      <form
        @submit.prevent="onSubmit"
        class="flex flex-col gap-6 mx-auto max-w-[485px]">
        <!-- Values must match tj_contact_categories() in inc/rest/contact-functions.php -->
        <FieldSelect
          name="category"
          label="What can we help you with?"
          placeholder="Select category"
          required>
          <template #options>
            <option value="login">I can't login</option>
            <option value="technical">I'm having technical difficulties</option>
            <option value="enquiry">I have another enquiry</option>
            <option value="complaint">I would like to make a complaint</option>
          </template>
        </FieldSelect>

        <FieldInputText
          label="Name"
          name="name"
          placeholder="Enter name"
          required />

        <FieldInputText
          type="tel"
          label="Contact Number"
          name="number"
          placeholder="Type contact number"
          :schema="numberSchema"
          required />

        <FieldInputText
          type="email"
          label="Email"
          name="email"
          placeholder="Enter email"
          required />

        <FieldTextarea label="Message" name="message" required />

        <TurnstileWidget
          v-if="turnstileSiteKey"
          ref="turnstile"
          :siteKey="turnstileSiteKey"
          action="contact"
          @token="turnstileToken = $event" />

        <Btn
          type="submit"
          gradient
          class="mt-30 w-full"
          :disabled="isLoading || !canSend">
          <span v-if="isLoading">Sending...</span>
          <span v-else>Send</span>
        </Btn>
      </form>
    </Box>
  </section>
</template>
